import { useEffect, useRef } from 'react';
import '../styles/contextual-drawer.css';

interface DecisionOption {
  id: string;
  label: string;
}

interface ContextualDrawerProps {
  title: string;
  content: string;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onClose: () => void;
  nextLabel: string;
  doneLabel: string;
  /** Optional decision options - when provided, renders choice buttons instead of next */
  decisionOptions?: DecisionOption[];
  /** Callback when a decision option is selected */
  onDecision?: (optionId: string) => void;
}

/**
 * A fixed bottom drawer for contextual help tours.
 * Styled to match Driver.js popover.
 * Pushes page content up instead of overlaying.
 */
export function ContextualDrawer({
  title,
  content,
  currentStep,
  totalSteps,
  onNext,
  onClose,
  nextLabel,
  doneLabel,
  decisionOptions,
  onDecision,
}: Readonly<ContextualDrawerProps>) {
  const isLastStep = currentStep === totalSteps - 1;
  const isDecisionMode = decisionOptions && decisionOptions.length > 0;
  const drawerRef = useRef<HTMLDivElement>(null);

  // Push page content up by adding padding to body
  // Note: Scrolling is handled by ContextualOverlay which accounts for drawer height
  useEffect(() => {
    const updateBodyPadding = () => {
      if (drawerRef.current) {
        const drawerHeight = drawerRef.current.offsetHeight;
        document.body.style.paddingBottom = `${drawerHeight}px`;
      }
    };

    updateBodyPadding();

    // Update on resize
    window.addEventListener('resize', updateBodyPadding);

    return () => {
      document.body.style.paddingBottom = '';
      window.removeEventListener('resize', updateBodyPadding);
    };
  }, [content]); // Re-calculate when content changes

  return (
    <div ref={drawerRef} className="showme-contextual-drawer">
      {/* Close button */}
      <button
        className="showme-contextual-drawer-close"
        onClick={onClose}
        aria-label="Fermer"
      >
        ×
      </button>

      {/* Title */}
      <div className="showme-contextual-drawer-title">
        {title}
      </div>

      {/* Progress */}
      <div className="showme-contextual-drawer-progress">
        {currentStep + 1} / {totalSteps}
      </div>

      {/* Content */}
      <div className="showme-contextual-drawer-description">
        {content}
      </div>

      {/* Footer with buttons */}
      <div className={`showme-contextual-drawer-footer ${isDecisionMode ? 'showme-contextual-drawer-footer--decision' : ''}`}>
        {isDecisionMode ? (
          /* Decision options */
          decisionOptions.map((option) => (
            <button
              key={option.id}
              className="showme-contextual-drawer-next-btn"
              onClick={() => onDecision?.(option.id)}
            >
              {option.label}
            </button>
          ))
        ) : (
          /* Navigation button (no previous - actions can't be undone) */
          <button
            className="showme-contextual-drawer-next-btn"
            onClick={onNext}
          >
            {isLastStep ? doneLabel : nextLabel}
          </button>
        )}
      </div>
    </div>
  );
}
